import React, { useState, useEffect, useCallback } from "react";
import { MemoryRouter, Route } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@material-ui/core";

import MessagesList from "../../../components/MessagesList";
import MessageInput from "../../../components/MessageInput";
import TransferTicketModal from "../../../components/TransferTicketModal";
import ConfirmationModal from "../../../components/ConfirmationModal";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { ReplyMessageProvider } from "../../../context/ReplyingMessage/ReplyingMessageContext";
import Avatar from "./Avatar";
import { slaChip, tagChip, chDot } from "./decorate";

// MessageInput lê ticketId de useParams() e posta em /messages/:ticketId — como
// a rota /crm-tickets não tem esse param, envolvemos só ele num MemoryRouter
// apontando para /:ticketId. MessagesList recebe ticketId por prop, sem router.
const ComposerWithRouter = ({ ticketId, ticketStatus }) => (
  <MemoryRouter
    initialEntries={[`/${ticketId}`]}
    key={ticketId}
    getUserConfirmation={() => {}}
  >
    <Route path="/:ticketId">
      <MessageInput ticketStatus={ticketStatus} />
    </Route>
  </MemoryRouter>
);

// Centro: header restilizado + chat real + barra de ações rápidas + composer real.
const Conversation = ({ V, ticket, lead, slaLimite, connected, onOpenQr }) => {
  const { user } = React.useContext(AuthContext);
  const [transferOpen, setTransferOpen] = useState(false);
  const [confirmAbandonOpen, setConfirmAbandonOpen] = useState(false);

  // Link de assinatura do pedido (ERP). O botão só aparece se available: true.
  const [salePortal, setSalePortal] = useState(null);
  const ticketId = ticket?.id;

  const fetchSalePortal = useCallback(async () => {
    if (!ticketId) {
      setSalePortal(null);
      return;
    }
    try {
      const { data } = await api.get(
        `/crm/tickets/${ticketId}/sale-portal-link`,
      );
      setSalePortal(data);
    } catch (err) {
      setSalePortal({ available: false });
    }
  }, [ticketId]);

  useEffect(() => {
    fetchSalePortal();
  }, [fetchSalePortal]);

  // Agendamento: compromisso combinado na conversa (ex.: dia de trazer o
  // carro). O chip mostra o próximo em aberto; remarcar = criar outro.
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleNotes, setScheduleNotes] = useState("");
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [nextAppointment, setNextAppointment] = useState(null);

  const fetchAppointments = useCallback(async () => {
    if (!ticketId) {
      setNextAppointment(null);
      return;
    }
    try {
      const { data } = await api.get(`/crm/tickets/${ticketId}/appointments`);
      const upcoming = (Array.isArray(data) ? data : [])
        .filter((a) => a.status === "scheduled")
        .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))[0];
      setNextAppointment(upcoming || null);
    } catch (err) {
      setNextAppointment(null);
    }
  }, [ticketId]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const createAppointment = async () => {
    if (!scheduleDate) {
      toast.warn("Escolha a data e a hora do agendamento");
      return;
    }
    setSavingSchedule(true);
    try {
      await api.post(`/crm/tickets/${ticketId}/appointments`, {
        scheduledAt: new Date(scheduleDate).toISOString(),
        notes: scheduleNotes,
      });
      toast.success(
        `Agendado para ${new Date(scheduleDate).toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })}`,
      );
      setScheduleOpen(false);
      setScheduleDate("");
      setScheduleNotes("");
      fetchAppointments();
    } catch (err) {
      toastError(err);
    }
    setSavingSchedule(false);
  };

  const cancelAppointment = async () => {
    if (!nextAppointment) return;
    try {
      await api.put(`/crm/appointments/${nextAppointment.id}`, {
        status: "cancelled",
      });
      toast.success("Agendamento cancelado");
      fetchAppointments();
    } catch (err) {
      toastError(err);
    }
  };

  if (!ticket) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 0,
        }}
      >
        <div style={{ fontSize: 13.5, color: V.labelMuted, fontWeight: 800 }}>
          Selecione um ticket para começar a conversar
        </div>
      </div>
    );
  }

  const name = ticket.contact?.name || ticket.contact?.number || "Sem nome";
  const phone = ticket.contact?.number || "";
  const sla = slaChip(ticket, slaLimite);
  const tag = tagChip(lead?.tag);
  const ch = chDot(lead?.channel);
  const funnel = V.key === "b";

  // Abrir venda: cria o pedido rascunho no ERP; o ticket segue aberto.
  const openSale = async () => {
    try {
      const { data } = await api.post(`/crm/tickets/${ticket.id}/open-sale`);
      if (data?.alreadyOpen) {
        toast.warn(`Venda ${data.order_number} já está aberta para este lead`);
      } else {
        toast.success("Venda aberta no ERP");
      }
      fetchSalePortal();
    } catch (err) {
      toastError(err);
    }
  };

  // Abandonar: fecha o ticket sem venda (mesmo payload do TicketActionButtons).
  const abandonTicket = async () => {
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: "closed",
        userId: user?.id || null,
      });
      toast.success("Ticket abandonado");
    } catch (err) {
      toastError(err);
    }
  };

  // Envia o link público de assinatura como mensagem no ticket (mesmo endpoint
  // e payload do MessageInput). O botão continua visível até o cliente assinar.
  const sendSignatureLink = async () => {
    if (!salePortal?.available) return;
    try {
      // Busca o link na hora do envio: o token pode ter expirado desde o
      // carregamento da tela (o ERP renova e devolve o link novo).
      const { data: fresh } = await api.get(
        `/crm/tickets/${ticket.id}/sale-portal-link`,
      );
      setSalePortal(fresh);
      if (!fresh?.available) {
        toast.warn("Link indisponível — verifique a venda no ERP");
        return;
      }
      await api.post(`/messages/${ticket.id}`, {
        read: 1,
        fromMe: true,
        mediaUrl: "",
        body:
          fresh.message ||
          `Olá! Segue o link para revisão e assinatura do pedido ${fresh.order_number}: ${fresh.url}`,
        quotedMsg: null,
      });
      toast.success("Link de assinatura enviado");
    } catch (err) {
      toastError(err);
    }
  };

  // Retornar: devolve o ticket p/ a fila (pending, sem atendente).
  const returnTicket = async () => {
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: "pending",
        userId: null,
      });
    } catch (err) {
      toastError(err);
    }
  };

  // Aceitar (pendente) / Reabrir (fechado): assume o ticket p/ o atendente.
  const takeTicket = async () => {
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: "open",
        userId: user?.id || null,
      });
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <ReplyMessageProvider>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 11,
            padding: "10px 16px",
            background: V.headerBg,
            borderBottom: V.headerBorder,
            flex: "none",
          }}
        >
          <Avatar
            url={ticket.contact?.profilePicUrl}
            name={name}
            size={36}
            radius={funnel ? 10 : "50%"}
            fontSize={12.5}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: V.textStrong }}>
              {name}
            </div>
            <div style={{ fontSize: 11, color: V.phoneText }}>
              {phone}
              {ch ? ` · ${ch.label}` : ""}
            </div>
          </div>
          {tag && (
            <span
              style={{
                background: tag.bg,
                color: tag.color,
                fontSize: 10,
                fontWeight: 800,
                padding: "2px 9px",
                borderRadius: V.tagRadius,
              }}
            >
              {tag.label}
            </span>
          )}
          <span
            style={{
              background: sla.bg,
              color: sla.text,
              fontSize: 10.5,
              fontWeight: 800,
              padding: "2px 9px",
              borderRadius: funnel ? 6 : 999,
            }}
          >
            {sla.label}
          </span>
          <span style={{ flex: 1 }} />
          {/* Ações do topo escondidas: Resolver/Retornar/Transferir vivem na barra
            inferior; excluir ticket fica fora da UI (ninguém deleta). */}
        </div>

        {/* Mensagens reais (paginação + socket appMessage). Restilizamos só o
          container; a lógica das bolhas é do componente original. */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            background: V.chatBg,
            overflow: "hidden",
          }}
        >
          <MessagesList ticketId={ticket.id} isGroup={false} />
        </div>

        {/* Barra de ações rápidas */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 7,
            padding: "7px 14px",
            background: V.quickBg,
            borderTop: V.quickBorder,
            flex: "none",
          }}
        >
          {!connected && (
            <button
              type="button"
              onClick={onOpenQr}
              title="Conectar celular via QR code"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "#FDE8E8",
                border: "1.5px solid #E5484D",
                color: "#C0392B",
                borderRadius: funnel ? 7 : 999,
                padding: "4px 12px",
                fontWeight: 800,
                fontSize: 11,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              <span
                style={{
                  width: 9,
                  height: 9,
                  border: "2.5px solid #E5484D",
                  borderRadius: 2,
                  flex: "none",
                }}
              />
              Conectar celular
            </button>
          )}
          <button
            type="button"
            disabled
            title="Ligar (em breve)"
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: funnel ? "#F4F6F9" : "#F4EFE4",
              border: funnel ? "1px solid #EAEEF3" : "1px solid #EDE4D1",
              color: funnel ? "#B6BFCB" : "#C4BBA6",
              borderRadius: funnel ? 7 : 999,
              padding: "5px 9px",
              cursor: "not-allowed",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
          </button>
          {ticket.status !== "closed" && (
            <button
              type="button"
              onClick={() => setTransferOpen(true)}
              style={{
                background: "#fff",
                border: funnel ? "1px solid #E2E7EE" : "1px solid #E8DFCE",
                color: V.sublabel,
                borderRadius: funnel ? 7 : 999,
                padding: "5px 12px",
                fontWeight: 800,
                fontSize: 11,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              Transferir
            </button>
          )}
          {ticket.status === "open" && (
            <button
              type="button"
              onClick={returnTicket}
              title="Retornar o ticket para a fila"
              style={{
                background: "#fff",
                border: funnel ? "1px solid #E2E7EE" : "1px solid #E8DFCE",
                color: V.sublabel,
                borderRadius: funnel ? 7 : 999,
                padding: "5px 12px",
                fontWeight: 800,
                fontSize: 11,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              Retornar
            </button>
          )}
          {nextAppointment && (
            <span
              title={nextAppointment.notes || "Agendamento em aberto"}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "#EAF2FD",
                color: "#1a56b0",
                borderRadius: funnel ? 7 : 999,
                padding: "4px 10px",
                fontWeight: 800,
                fontSize: 11,
              }}
            >
              {`Agendado ${new Date(nextAppointment.scheduledAt).toLocaleString(
                "pt-BR",
                {
                  day: "2-digit",
                  month: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                },
              )}`}
              <button
                type="button"
                onClick={cancelAppointment}
                title="Cancelar este agendamento"
                style={{
                  background: "none",
                  border: "none",
                  color: "inherit",
                  cursor: "pointer",
                  fontWeight: 900,
                  fontSize: 12,
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </span>
          )}
          <span style={{ flex: 1 }} />
          {salePortal?.available && (
            <button
              type="button"
              onClick={sendSignatureLink}
              title="Enviar o link público de assinatura do pedido no chat"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "#fff",
                border: funnel ? "1px solid #E2E7EE" : "1px solid #E8DFCE",
                color: V.sublabel,
                borderRadius: funnel ? 7 : 999,
                padding: "5px 12px",
                fontWeight: 800,
                fontSize: 11,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
              </svg>
              Enviar link de assinatura
            </button>
          )}
          {(() => {
            const primary = {
              background: "#1F9D57",
              border: "none",
              color: "#fff",
              borderRadius: funnel ? 7 : 999,
              padding: "5px 14px",
              fontWeight: 900,
              fontSize: 11,
              fontFamily: "inherit",
              cursor: "pointer",
            };
            if (ticket.status === "pending") {
              return (
                <button
                  type="button"
                  onClick={takeTicket}
                  title="Aceitar e começar o atendimento"
                  style={primary}
                >
                  Aceitar
                </button>
              );
            }
            if (ticket.status === "closed") {
              return (
                <button
                  type="button"
                  onClick={takeTicket}
                  title="Reabrir o ticket"
                  style={primary}
                >
                  Reabrir
                </button>
              );
            }
            return (
              <>
                <button
                  type="button"
                  onClick={() => setConfirmAbandonOpen(true)}
                  title="Encerrar o atendimento sem venda"
                  style={{ ...primary, background: "#d32f2f" }}
                >
                  Abandonar Ticket
                </button>
                <button
                  type="button"
                  onClick={() => setScheduleOpen(true)}
                  title="Agendar um compromisso com este cliente"
                  style={{ ...primary, background: "#1a56b0" }}
                >
                  Agendamento
                </button>
                <button
                  type="button"
                  onClick={openSale}
                  title="Abrir a venda no ERP (o atendimento segue aberto)"
                  style={primary}
                >
                  Abrir venda
                </button>
              </>
            );
          })()}
        </div>

        {/* Composer real (envia via provider WhatsApp) */}
        <ComposerWithRouter ticketId={ticket.id} ticketStatus={ticket.status} />

        <TransferTicketModal
          modalOpen={transferOpen}
          onClose={() => setTransferOpen(false)}
          ticketid={ticket.id}
          ticketWhatsappId={ticket.whatsappId}
        />

        <ConfirmationModal
          title="Abandonar Ticket"
          open={confirmAbandonOpen}
          onClose={setConfirmAbandonOpen}
          onConfirm={abandonTicket}
        >
          Abandonar este atendimento? O ticket será fechado.
        </ConfirmationModal>

        <Dialog
          open={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Agendar com {name}</DialogTitle>
          <DialogContent
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            <TextField
              label="Data e hora"
              type="datetime-local"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              autoFocus
            />
            <TextField
              label="Observações"
              placeholder="Trazer o carro para remap às 9h…"
              value={scheduleNotes}
              onChange={(e) => setScheduleNotes(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScheduleOpen(false)} color="default">
              Cancelar
            </Button>
            <Button
              onClick={createAppointment}
              color="primary"
              variant="contained"
              disabled={savingSchedule || !scheduleDate}
            >
              {savingSchedule ? "Agendando…" : "Agendar"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ReplyMessageProvider>
  );
};

export default Conversation;
